import routerConstants from '@/constants/routerConstants'
import { getDanhSachNhomKhachHang } from '@/apis/nhomKhachHangApi'
import type { NhomKhachHangUiTestItem } from '@/modules/ui-test/catalogs/nhomKhachHangUiTestCatalog'
import {
  buildNhomKhachHangUiTestPlan,
  describeNhomKhachHangUiTestPlan,
  generateEditMa,
  refreshNhomKhachHangUiTestPlan,
  type NhomKhachHangUiTestPlan,
} from '@/modules/ui-test/scenarios/nhomKhachHangUiTestPlan'
import {
  clickDialogConfirmButton,
  clickDialogSaveButton,
  clickElement,
  fillFieldValue,
  findFieldByName,
  findInRoot,
  findRowByMa,
  pressEnter,
  typeSlowly,
  waitFor,
  waitForAsync,
  waitForDialogClosed,
  waitForTopDialog,
} from '@/modules/ui-test/utils/uiTestDomActions'
import { runUiTestSuite, type UiTestRunner } from '@/modules/ui-test/utils/uiTestRunner'
import { buildMaSet, buildTenSet, normKey, stamp } from '@/modules/test-data/testDataSeedUtils'

const ADD_BTN = '[data-ui-test-id="nkh-add-btn"]'
const SEARCH_BAR = '[data-ui-test-id="nkh-search-bar"]'
const LIST_PATH = routerConstants.nhomKhachHang.list

function screenOf(uiTest: UiTestRunner): HTMLElement {
  const screen = uiTest.screenRootRef.current
  if (!screen) throw new Error('Không tìm thấy vùng màn hình nhóm khách hàng')
  return screen
}

function rowEditSelector(ma: string): string {
  return `[data-ui-test-id="nkh-edit-${CSS.escape(ma)}"] button`
}

function rowDeleteSelector(ma: string): string {
  return `[data-ui-test-id="nkh-delete-${CSS.escape(ma)}"] button`
}

async function waitForListReady(uiTest: UiTestRunner): Promise<void> {
  await waitFor(
    () => {
      const screen = uiTest.screenRootRef.current
      if (!screen) return false
      if (screen.querySelector(ADD_BTN)) return true
      if (screen.querySelector(SEARCH_BAR)) return true
      return Boolean(screen.querySelector('[role="progressbar"]') === null && screen.querySelector('table'))
    },
    20_000,
    200,
  )
}

async function goToCleanList(uiTest: UiTestRunner): Promise<void> {
  if (window.location.pathname !== LIST_PATH || window.location.search) {
    uiTest.navigate(LIST_PATH, { replace: true })
    await waitFor(
      () => window.location.pathname === LIST_PATH && !window.location.search,
      8000,
      100,
    )
  }
  await waitForListReady(uiTest)
}

async function createOneViaUi(
  uiTest: UiTestRunner,
  group: NhomKhachHangUiTestItem,
): Promise<{ ok: boolean; message: string }> {
  const screen = screenOf(uiTest)
  const addBtn = findInRoot(screen, ADD_BTN)
  if (!addBtn) return { ok: false, message: 'Không tìm thấy nút Thêm' }

  uiTest.appendLog({
    id: `add-click-${group.ma}`,
    label: 'Thêm mới',
    status: 'running',
    message: `Thêm ${group.ma}`,
  })
  addBtn.click()
  await waitFor(() => document.querySelectorAll('[role="dialog"]').length > 0, 8_000, 80)

  let dialog: HTMLElement
  try {
    dialog = await waitForTopDialog()
  } catch {
    return { ok: false, message: 'Modal thêm không mở' }
  }

  const maField = findFieldByName(dialog, 'ma')
  const tenField = findFieldByName(dialog, 'tenNhom')
  const ghiChuField = findFieldByName(dialog, 'ghiChu')
  if (!maField || !tenField) return { ok: false, message: 'Không tìm thấy ô Mã / Tên' }

  await typeSlowly(maField, group.ma)
  await typeSlowly(tenField, group.tenNhom)
  const ghiChu = group.ghiChu ?? 'Sinh tự động — test thao tác UI'
  if (ghiChuField) await typeSlowly(ghiChuField, ghiChu)

  try {
    await clickDialogSaveButton(dialog)
    await waitForDialogClosed(dialog)
    return { ok: true, message: `Đã tạo ${group.ma}` }
  } catch (e) {
    return { ok: false, message: (e as { message?: string })?.message || `Lưu thất bại: ${group.ma}` }
  }
}

function buildNhomKhachHangFeatures(plan: NhomKhachHangUiTestPlan) {
  return [
    {
      id: 'list',
      label: 'Danh sách',
      run: async (uiTest: UiTestRunner) => {
        await goToCleanList(uiTest)
        const screen = screenOf(uiTest)
        const table = screen.querySelector('table')
        if (!table) return { status: 'fail' as const, message: 'Không thấy bảng danh sách' }
        const dataRows = screen.querySelectorAll('tbody tr[data-ui-test-row-ma]')
        if (dataRows.length > 0) {
          return { status: 'ok' as const, message: `Bảng hiển thị ${dataRows.length} dòng (trang hiện tại)` }
        }
        return { status: 'ok' as const, message: 'Bảng đã tải — không có dòng trên trang hiện tại' }
      },
    },
    {
      id: 'create',
      label: 'Thêm mới',
      run: async (uiTest: UiTestRunner) => {
        await goToCleanList(uiTest)
        const { data: groups } = await getDanhSachNhomKhachHang()
        const existingMa = buildMaSet(groups.map((g) => ({ ma: g.ma })))
        const existingTen = buildTenSet(groups)

        let created = 0
        let skipped = 0
        let failed = 0

        for (const group of plan.createItems) {
          if (existingMa.has(normKey(group.ma)) || existingTen.has(normKey(group.tenNhom))) {
            skipped += 1
            continue
          }
          const result = await createOneViaUi(uiTest, group)
          if (result.ok) {
            created += 1
            existingMa.add(normKey(group.ma))
            existingTen.add(normKey(group.tenNhom))
            uiTest.notifyDataChanged()
          } else {
            failed += 1
            uiTest.appendLog({
              id: `add-fail-${group.ma}`,
              label: 'Thêm mới',
              status: 'fail',
              message: result.message,
            })
          }
        }

        await refreshNhomKhachHangUiTestPlan(plan)
        uiTest.appendLog({
          id: 'plan-after-create',
          label: 'Kế hoạch',
          status: 'ok',
          message: describeNhomKhachHangUiTestPlan(plan),
        })

        if (failed > 0) {
          return {
            status: 'fail' as const,
            message: `Tạo ${created}, bỏ qua ${skipped}, lỗi ${failed} / ${plan.createItems.length}`,
          }
        }
        return {
          status: 'ok' as const,
          message: `Tạo ${created}, bỏ qua ${skipped} (đã có) / ${plan.createItems.length} nhóm — ${describeNhomKhachHangUiTestPlan(plan)}`,
        }
      },
    },
    {
      id: 'search',
      label: 'Tìm kiếm',
      run: async (uiTest: UiTestRunner) => {
        const probeMa = plan.probeMa
        if (!probeMa) {
          return { status: 'skip' as const, message: 'Không có mã nhóm trên DB để tìm kiếm' }
        }

        await goToCleanList(uiTest)
        const screen = screenOf(uiTest)
        const searchBox = findInRoot(screen, SEARCH_BAR)
        const input = (searchBox?.querySelector('input') ?? screen.querySelector(`${SEARCH_BAR} input`)) as
          | HTMLInputElement
          | null
        if (!input) return { status: 'fail' as const, message: 'Không tìm thấy ô tìm kiếm' }

        input.focus()
        await clickElement(input)
        await typeSlowly(input, probeMa)
        input.focus()
        await pressEnter(input)

        await waitFor(() => Boolean(findRowByMa(screen, probeMa)), 10_000, 200)
        const row = findRowByMa(screen, probeMa)
        if (!row) {
          return {
            status: 'skip' as const,
            message: `Không thấy dòng ${probeMa} sau khi lọc`,
          }
        }
        return { status: 'ok' as const, message: `Tìm "${probeMa}" — thấy đúng 1 dòng` }
      },
    },
    {
      id: 'view',
      label: 'Xem / chọn dòng',
      run: async (uiTest: UiTestRunner) => {
        const probeMa = plan.probeMa
        if (!probeMa) {
          return { status: 'skip' as const, message: 'Không có dòng trên DB để chọn xem' }
        }

        await goToCleanList(uiTest)
        uiTest.notifyDataChanged()
        await waitFor(() => Boolean(findRowByMa(screenOf(uiTest), probeMa)), 10_000, 200)
        const screen = screenOf(uiTest)
        const row = findRowByMa(screen, probeMa)
        if (!row) {
          return { status: 'skip' as const, message: `Không có dòng ${probeMa} để chọn xem` }
        }

        await clickElement(row)
        await waitFor(() => row.getAttribute('data-selected') === 'true', 3_000, 100)
        if (row.getAttribute('data-selected') !== 'true') {
          return { status: 'fail' as const, message: 'Nhấn dòng nhưng không thấy trạng thái chọn' }
        }

        const cells = row.querySelectorAll('td')
        const text = Array.from(cells)
          .map((c) => c.textContent?.trim())
          .filter(Boolean)
          .join(' · ')
        return { status: 'ok' as const, message: `Đã chọn dòng ${probeMa} — ${text || probeMa}` }
      },
    },
    {
      id: 'edit',
      label: 'Sửa',
      run: async (uiTest: UiTestRunner) => {
        const probeMa = plan.probeMa
        if (!probeMa) {
          return { status: 'skip' as const, message: 'Không có dòng trên DB để sửa' }
        }

        const { data: groupsBefore } = await getDanhSachNhomKhachHang()
        const probeId =
          plan.probeId ??
          groupsBefore.find((g) => normKey(g.ma) === normKey(probeMa))?.id ??
          null
        if (probeId) plan.probeId = probeId

        await goToCleanList(uiTest)
        const screen = screenOf(uiTest)
        const row = findRowByMa(screen, probeMa)
        if (!row) return { status: 'skip' as const, message: `Không có ${probeMa} để sửa` }

        const editBtn = row.querySelector(rowEditSelector(probeMa)) as HTMLElement | null
        if (!editBtn) return { status: 'fail' as const, message: 'Không tìm thấy nút Sửa trên dòng' }

        await clickElement(editBtn)
        try {
          await waitFor(() => window.location.pathname.includes('/edit/'), 12_000, 150)
        } catch {
          return { status: 'fail' as const, message: 'Không chuyển sang trang sửa' }
        }

        let dialog: HTMLElement
        try {
          dialog = await waitForTopDialog()
        } catch {
          return { status: 'fail' as const, message: 'Trang / modal sửa không mở' }
        }

        try {
          await waitFor(() => {
            const maInput = findFieldByName(dialog, 'ma') as HTMLInputElement | null
            if (!maInput || maInput.disabled) return false
            const actions = dialog.querySelector('.MuiDialogActions-root')
            const saveBtn = actions?.querySelector(
              'button.MuiButton-containedPrimary, button.MuiButton-contained',
            ) as HTMLButtonElement | null
            return Boolean(saveBtn && !saveBtn.disabled && maInput.value.trim())
          }, 15_000, 150)
        } catch {
          return {
            status: 'fail' as const,
            message: 'Form sửa chưa sẵn sàng (đang tải hoặc nút Lưu bị khóa)',
          }
        }

        const ghiChuField = findFieldByName(dialog, 'ghiChu')
        const maField = findFieldByName(dialog, 'ma')
        if (!maField) return { status: 'fail' as const, message: 'Không tìm thấy ô Mã trong form sửa' }
        if ((maField as HTMLInputElement).disabled) {
          return { status: 'fail' as const, message: 'Ô Mã đang bị khóa — cần cho phép sửa mã (liên kết theo id)' }
        }

        const existingMa = buildMaSet(groupsBefore.map((g) => ({ ma: g.ma })))
        const newMa = generateEditMa(probeMa, existingMa)

        await fillFieldValue(maField as HTMLInputElement, newMa)
        if (ghiChuField) {
          await fillFieldValue(ghiChuField, `Đã sửa UI test — ${stamp()}`)
        }

        try {
          await waitFor(
            () => (findFieldByName(dialog, 'ma') as HTMLInputElement | null)?.value === newMa,
            5_000,
            100,
          )
        } catch {
          return { status: 'fail' as const, message: 'Giá trị ô Mã không khớp sau khi nhập — state form chưa cập nhật' }
        }

        try {
          await clickDialogSaveButton(dialog)
          await waitForDialogClosed(dialog, 20_000)
        } catch (e) {
          return { status: 'fail' as const, message: (e as { message?: string })?.message || 'Lưu sửa thất bại' }
        }

        try {
          await waitFor(() => window.location.pathname === LIST_PATH, 12_000, 200)
        } catch {
          return { status: 'fail' as const, message: 'Lưu xong nhưng không quay về danh sách' }
        }

        try {
          await waitForAsync(async () => {
            const { data } = await getDanhSachNhomKhachHang()
            if (probeId) {
              const updated = data.find((g) => g.id === probeId)
              return Boolean(updated && normKey(updated.ma) === normKey(newMa))
            }
            return data.some((g) => normKey(g.ma) === normKey(newMa))
          }, 15_000, 300)
        } catch {
          return {
            status: 'fail' as const,
            message: `API chưa phản ánh mã mới ${newMa} — kiểm tra backend đã restart và capNhat có cập nhật ma`,
          }
        }

        plan.probeMa = newMa
        uiTest.notifyDataChanged()
        await waitForListReady(uiTest)

        const rowAfter = findRowByMa(screenOf(uiTest), newMa)
        if (rowAfter) {
          return { status: 'ok' as const, message: `Đã đổi mã ${probeMa} → ${newMa} (id giữ nguyên)` }
        }
        return {
          status: 'ok' as const,
          message: `Đã đổi mã ${probeMa} → ${newMa} (xác nhận qua API)`,
        }
      },
    },
    {
      id: 'delete',
      label: 'Xóa',
      run: async (uiTest: UiTestRunner) => {
        const deleteMa = plan.deleteMa
        if (!deleteMa) {
          return { status: 'skip' as const, message: 'Không có dòng phù hợp trên DB để xóa' }
        }
        if (plan.probeMa && normKey(deleteMa) === normKey(plan.probeMa)) {
          return {
            status: 'skip' as const,
            message: `Chỉ có một nhóm (${deleteMa}) — bỏ qua xóa để giữ dữ liệu tìm/sửa`,
          }
        }

        await goToCleanList(uiTest)
        const screen = screenOf(uiTest)
        await waitFor(() => Boolean(findRowByMa(screen, deleteMa)), 10_000, 200)

        let row = findRowByMa(screen, deleteMa)
        if (!row) {
          return { status: 'skip' as const, message: `Không có ${deleteMa} trên bảng để xóa` }
        }

        const deleteBtn = row.querySelector(rowDeleteSelector(deleteMa)) as HTMLElement | null
        if (!deleteBtn) return { status: 'fail' as const, message: 'Không tìm thấy nút Xóa trên dòng' }

        await clickElement(deleteBtn)
        const confirmDialog = await waitForTopDialog()
        await clickDialogConfirmButton(confirmDialog)
        await waitForDialogClosed(confirmDialog)

        uiTest.notifyDataChanged()
        await waitForListReady(uiTest)
        await waitFor(() => !findRowByMa(screenOf(uiTest), deleteMa), 15_000, 300)
        row = findRowByMa(screenOf(uiTest), deleteMa)
        if (row) return { status: 'fail' as const, message: `Xóa xong nhưng vẫn thấy ${deleteMa} trên bảng` }

        return { status: 'ok' as const, message: `Đã xóa ${deleteMa} và xác nhận biến mất khỏi danh sách` }
      },
    },
  ]
}

export const runNhomKhachHangUiTest = async (uiTest: UiTestRunner) => {
  const { data: groups } = await getDanhSachNhomKhachHang()
  const plan = buildNhomKhachHangUiTestPlan(groups)

  uiTest.appendLog({
    id: 'plan-init',
    label: 'Kế hoạch',
    status: 'running',
    message: describeNhomKhachHangUiTestPlan(plan),
  })

  return runUiTestSuite(uiTest, buildNhomKhachHangFeatures(plan), 'Nhóm khách hàng')
}

export const nhomKhachHangUiTestFeatureCount = buildNhomKhachHangFeatures({
  createItems: [],
  probeMa: null,
  probeId: null,
  deleteMa: null,
}).length
